import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import { useGetAffiliationList } from '~/hooks/apis/agent/useGetAffiliationList';
import { useGetAgency } from '~/hooks/apis/agent/useGetAgency';
import { useListAgencyUsers } from '~/hooks/apis/agent/useListAgencyUsers';
import { useUpdateAgency } from '~/hooks/apis/agent/useUpdateAgency';

import { Agency } from '~/types/services/agentHub';

import AgencyDetails from './AgencyDetails';
import AgencyEditor from './AgencyEditor';

const DEFAULT_SIZE_PER_PAGE = 10;

interface Props {
  id: string;
  onClose?: () => void;
  opened?: boolean;
  onUpdate?: () => void;
}

export default function AgencyDetailsModal({ id, onClose, opened = false, onUpdate }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState<boolean>(opened);
  const [page, setPage] = useState<number>(0);
  const [isEditing, setIsEditing] = useState(false);

  const {
    data: agencyData,
    isFetching: isLoadingAgency,
    isSuccess: isAgencySuccess,
  } = useGetAgency(id, { enabled: open && !!id });

  const { data: { rows: users = [], total = 0 } = {}, isFetching: isLoadingUsers } = useListAgencyUsers(
    {
      agencyId: id,
      page: page.toString(),
      pageSize: DEFAULT_SIZE_PER_PAGE.toString(),
    },
    { enabled: open && !!id },
  );

  const { data: availableAffiliations = [] } = useGetAffiliationList(undefined, { enabled: open });

  const updateAgencyMutation = useUpdateAgency({
    onSuccess: () => {
      enqueueSnackbar('Register updated successfully');
      setIsEditing(false);
      onUpdate?.();
    },
    onError: () => {
      enqueueSnackbar('There was an error updating the agency', { variant: 'error' });
    },
  });

  const onSave = useCallback(
    (editedData: Partial<Agency>) => {
      if (Object.entries(editedData).some(([_, value]) => !value)) {
        enqueueSnackbar('Please fill all the fields', { variant: 'error' });
        return;
      }

      const { id, name, businessCodeValue, address, city, state, postcode, country, affiliation } = editedData;
      updateAgencyMutation.mutate({
        agencyId: id,
        data: {
          name,
          businessCodeValue,
          address,
          city,
          state,
          postcode,
          country,
          affiliation,
        },
      });
    },
    [enqueueSnackbar, updateAgencyMutation],
  );

  const onCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
    setIsEditing(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (opened) {
      setOpen(true);
    }
  }, [opened]);

  const isLoading = isLoadingAgency;
  const loadingUserState: Utils.FetchingState = isLoadingUsers ? 'loading' : 'success';
  const isSaving = updateAgencyMutation.isPending;

  return (
    <Dialog onClose={closeModal} open={open}>
      <DialogTitle>Agency Details</DialogTitle>

      <DialogContent>
        {isLoading && <Spinner />}
        {isAgencySuccess && isEditing && agencyData && (
          <AgencyEditor
            availableAffiliations={availableAffiliations}
            data={agencyData}
            onSave={onSave}
            onCancel={onCancel}
            loadingState={isSaving ? 'loading' : 'success'}
          />
        )}
        {isAgencySuccess && !isEditing && (
          <AgencyDetails
            data={agencyData}
            users={users}
            page={page}
            setPage={setPage}
            total={total}
            loadingUserState={loadingUserState}
          />
        )}
      </DialogContent>

      <DialogActions>
        {!isEditing && (
          <Button variant="text" onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        )}
        <Button variant="text" onClick={closeModal}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
